import { type AccountRequest, account, passkeysSupported } from "./account";
import {
	type ClipHost,
	createClipHost,
	installAudioTap,
	openClipUrl,
} from "./clip";

// Before Unity's loader creates its AudioContext: lets a clip carry the game's
// sound (client/clip.ts). ?clipAudio=0 leaves Web Audio untouched.
if (new URLSearchParams(location.search).get("clipAudio") !== "0")
	installAudioTap();

// The game page's loader: starts the Unity WebGL build named by
// window.HEAVENSTEEL_LOADER (inlined by the Worker), shows progress, and offers
// the helpers the Unity jslib calls (window.HEAVENSTEEL_HOST). The page ↔ Unity
// contract (window.HEAVENSTEEL, window.HEAVENSTEEL_STATUS) is in common.md and
// docs/deploy.md; the Worker sets both before this script runs.

interface Variant {
	loaderUrl: string;
	dataUrl: string;
	frameworkUrl: string;
	codeUrl: string;
	symbolsUrl: string | null;
}

interface LoaderConfig extends Variant {
	version: string;
	base: string;
	streamingAssetsUrl: string;
	mobile: Variant | null;
	companyName: string;
	productName: string;
	productVersion: string;
}

interface Status {
	scene: string;
	phase: string | null;
	round: number;
	seat: number | null;
	hp: [number, number];
	result: unknown;
	error: string | null;
	[k: string]: unknown;
}

interface UnityInstance {
	SetFullscreen(on: 0 | 1): void;
	SendMessage(object: string, method: string, arg?: string | number): void;
	Quit(): Promise<void>;
}

type CreateUnityInstance = (
	canvas: HTMLCanvasElement,
	config: Record<string, unknown>,
	onProgress: (p: number) => void,
) => Promise<UnityInstance>;

declare global {
	interface Window {
		HEAVENSTEEL: {
			api: string;
			ws: string;
			room: string | null;
			build: string | null;
			autoplay: boolean;
			name: string | null;
			site: string;
			mode: "queue" | "challenge" | "practice" | null;
			difficulty: "easy" | "normal" | "hard" | null;
			/** /r/:code: the replay to play (docs/replays.md). */
			replay: string | null;
			/** /r/:code?speed=: 0.5, 1, 2 or 4. */
			speed: number | null;
		};
		HEAVENSTEEL_STATUS: Status;
		HEAVENSTEEL_LOADER: LoaderConfig | null;
		HEAVENSTEEL_ENV: string;
		HEAVENSTEEL_HOST: {
			copyText(text: string): Promise<boolean>;
			share(
				title: string,
				url: string,
			): Promise<"shared" | "copied" | "failed">;
			setStatus(patch: Partial<Status>): void;
			toast(message: string): void;
			/** A sign-in ceremony (client/account.ts); the answer goes to Unity as SendMessage("hs_session", "OnPage", json). */
			account(request: string): void;
			/** Whether this browser can offer passkeys. */
			passkeys: boolean;
			/** The highlight-clip recorder (client/clip.ts, docs/replays.md). */
			clip: ClipHost;
			/** Show the clip modal for a remote mp4 (the server-rendered fallback). */
			openClipUrl(url: string, title?: string): void;
		};
		HEAVENSTEEL_INSTANCE?: UnityInstance;
		/** The same instance under Unity's usual name (the e2e calls SendMessage on it). */
		unityInstance?: UnityInstance;
		createUnityInstance?: CreateUnityInstance;
	}
}

const $ = <T extends HTMLElement>(sel: string) =>
	document.querySelector(sel) as T;

const canvas = $<HTMLCanvasElement>("#unity-canvas");
const loading = $<HTMLElement>("#loading");
const fatal = $<HTMLElement>("#fatal");
const rotate = $<HTMLElement>("#rotate");
const toastEl = $<HTMLElement>("#toast");
const bar = $<HTMLElement>("#loading .bar");
const barFill = $<HTMLElement>("#loading .bar-fill");
const loadingText = $<HTMLElement>("#loading .loading-text");
const loadingPct = $<HTMLElement>("#loading .loading-pct");
const tipEl = $<HTMLElement>("#loading .tip");

const TIPS = [
	"Deployment is hidden: your opponent sees your army only when the battle starts.",
	"From round 2, pick one of four reinforcement cards before you deploy.",
	"Squads earn XP from damage and kills, and keep it between rounds.",
	"Losing a round costs HP: the more of the winner's army survives, the more it costs.",
	"Tech what you have, or buy something new? That's the whole game.",
	"Matches end when a side reaches 0 HP, or after round 20.",
	"Lost the connection? Reload: the match waits 60 seconds for you.",
];

// ── status ──────────────────────────────────────────────────────────────

function setStatus(patch: Partial<Status>): void {
	window.HEAVENSTEEL_STATUS = { ...window.HEAVENSTEEL_STATUS, ...patch };
	window.dispatchEvent(
		new CustomEvent("heavensteel:status", {
			detail: window.HEAVENSTEEL_STATUS,
		}),
	);
}

const coarse = window.matchMedia("(pointer: coarse)").matches;

// ── error reports (POST /v1/client-errors, docs/lanes/reliability.md) ──
// What Unity's own exception hook (HSApp) can't see: the build failing to load, Unity's error
// banners (a wasm abort, out of memory), uncaught page errors, a lost WebGL context, and the page
// closing in the middle of a live match (the forfeit triage needs to tell quits from crashes).
// At most 5 reports per page load; never blocks or throws.

let reportsLeft = 5;
function reportError(kind: string, message: string, stack?: string): void {
	const api = window.HEAVENSTEEL?.api;
	if (reportsLeft <= 0 || !api || api === "local") return;
	reportsLeft--;
	const s = window.HEAVENSTEEL_STATUS;
	const nav = navigator as Navigator & { deviceMemory?: number };
	const body = JSON.stringify({
		message: `${kind}: ${message}`.slice(0, 1000),
		stack: stack?.slice(0, 4000),
		version:
			window.HEAVENSTEEL_LOADER?.version ??
			window.HEAVENSTEEL.build ??
			undefined,
		userAgent: navigator.userAgent.slice(0, 300),
		url: location.pathname,
		room: window.HEAVENSTEEL.room ?? undefined,
		context: {
			source: "page",
			kind,
			scene: s?.scene,
			phase: s?.phase,
			round: s?.round,
			mobile: coarse,
			deviceMemory: nav.deviceMemory ?? null,
			visibility: document.visibilityState,
			uptimeSec: Math.round(performance.now() / 1000),
		},
	});
	try {
		void fetch(`${api}/v1/client-errors`, {
			method: "POST",
			headers: { "content-type": "application/json" },
			body,
			keepalive: true,
			mode: "cors",
		}).catch(() => {});
	} catch {}
}

const liveMatch = () => {
	const s = window.HEAVENSTEEL_STATUS;
	return (
		s?.scene === "match" && !!s.phase && s.phase !== "matchEnded" && !s.result
	);
};

window.addEventListener("error", (e) =>
	reportError(
		"pageError",
		e.message || "error",
		(e.error as Error | undefined)?.stack,
	),
);
window.addEventListener("unhandledrejection", (e) => {
	const r = e.reason as Error | string | undefined;
	reportError(
		"unhandledRejection",
		r instanceof Error ? r.message : String(r),
		r instanceof Error ? r.stack : undefined,
	);
});
window.addEventListener("pagehide", () => {
	if (liveMatch())
		reportError("leftLiveMatch", "the page closed during a live match");
});

// ── toast, copy, share (called by the Unity jslib when present) ─────────

let toastTimer = 0;
function toast(message: string): void {
	toastEl.textContent = message;
	toastEl.hidden = false;
	toastEl.classList.add("show");
	window.clearTimeout(toastTimer);
	toastTimer = window.setTimeout(() => {
		toastEl.classList.remove("show");
		window.setTimeout(() => {
			toastEl.hidden = true;
		}, 250);
	}, 2400);
}

function legacyCopy(text: string): boolean {
	const ta = document.createElement("textarea");
	ta.value = text;
	ta.setAttribute("readonly", "");
	ta.style.position = "fixed";
	ta.style.opacity = "0";
	document.body.appendChild(ta);
	ta.select();
	ta.setSelectionRange(0, text.length);
	let ok = false;
	try {
		ok = document.execCommand("copy");
	} catch {
		ok = false;
	}
	ta.remove();
	return ok;
}

async function copyText(text: string): Promise<boolean> {
	let ok = false;
	try {
		if (navigator.clipboard && window.isSecureContext) {
			await navigator.clipboard.writeText(text);
			ok = true;
		}
	} catch {
		ok = false;
	}
	if (!ok) ok = legacyCopy(text);
	// a link (share) or any other text the game copies (the player id in Settings)
	toast(ok ? (/^https?:\/\//.test(text) ? "Link copied" : "Copied") : text);
	return ok;
}

async function share(
	title: string,
	url: string,
): Promise<"shared" | "copied" | "failed"> {
	if (coarse && typeof navigator.share === "function") {
		try {
			await navigator.share({ title, url });
			return "shared";
		} catch (e) {
			if ((e as DOMException)?.name === "AbortError") return "failed";
		}
	}
	return (await copyText(url)) ? "copied" : "failed";
}

function runAccount(request: string): void {
	let req: AccountRequest;
	try {
		req = JSON.parse(request) as AccountRequest;
	} catch {
		return;
	}
	void account(req).then((result) => {
		try {
			window.HEAVENSTEEL_INSTANCE?.SendMessage(
				"hs_session",
				"OnPage",
				JSON.stringify(result),
			);
		} catch (e) {
			console.warn("[heavensteel] account result not delivered", e);
		}
	});
}

window.HEAVENSTEEL_HOST = {
	copyText,
	share,
	setStatus,
	toast,
	account: runAccount,
	passkeys: passkeysSupported(),
	clip: createClipHost({
		canvas,
		toast,
		copyText,
		share,
		setStatus: (patch) => setStatus(patch as Partial<Status>),
	}),
	openClipUrl,
};

// ── overlays ────────────────────────────────────────────────────────────

function showFatal(title: string, text: string, code: string): void {
	setStatus({ error: code });
	loading.hidden = true;
	$<HTMLElement>("#fatal .fatal-title").textContent = title;
	$<HTMLElement>("#fatal .fatal-text").textContent = text;
	fatal.hidden = false;
}

document.addEventListener("click", (e) => {
	const action = (e.target as HTMLElement | null)
		?.closest("[data-action]")
		?.getAttribute("data-action");
	if (action === "reload") window.location.reload();
	if (action === "dismiss-rotate") {
		try {
			sessionStorage.setItem("hs.rotateDismissed", "1");
		} catch {}
		rotate.hidden = true;
	}
});

function updateRotateHint(): void {
	let dismissed = false;
	try {
		dismissed = sessionStorage.getItem("hs.rotateDismissed") === "1";
	} catch {}
	const portrait = window.matchMedia("(orientation: portrait)").matches;
	rotate.hidden = !(coarse && portrait && !dismissed);
}

let progressShown = 0;
function setProgress(p: number): void {
	const pct = Math.max(progressShown, Math.min(100, Math.round(p * 100)));
	progressShown = pct;
	barFill.style.transform = `scaleX(${pct / 100})`;
	bar.setAttribute("aria-valuenow", String(pct));
	loadingPct.textContent = `${pct}%`;
	loadingText.textContent =
		pct < 90
			? window.HEAVENSTEEL?.replay
				? "Loading the replay…"
				: "Downloading the battlefield…"
			: "Starting the engine…";
	setStatus({ progress: pct });
}

let tipIndex = Math.floor(Math.random() * TIPS.length);
function nextTip(): void {
	tipEl.textContent = TIPS[tipIndex % TIPS.length];
	tipIndex++;
}

// ── Unity ───────────────────────────────────────────────────────────────

/** Phones get the ASTC build when there is one and the GPU has no BCn/DXT. */
function pickVariant(cfg: LoaderConfig): Variant {
	if (!cfg.mobile) return cfg;
	const forced = new URLSearchParams(location.search).get("variant");
	if (forced === "mobile") return cfg.mobile;
	if (forced === "desktop") return cfg;
	try {
		const gl = document.createElement("canvas").getContext("webgl2");
		if (!gl) return cfg;
		const s3tc = !!gl.getExtension("WEBGL_compressed_texture_s3tc");
		const astc = !!gl.getExtension("WEBGL_compressed_texture_astc");
		return !s3tc && astc ? cfg.mobile : cfg;
	} catch {
		return cfg;
	}
}

function hasWebGL2(): boolean {
	try {
		return !!document.createElement("canvas").getContext("webgl2");
	} catch {
		return false;
	}
}

function loadScript(src: string): Promise<void> {
	return new Promise((resolve, reject) => {
		const s = document.createElement("script");
		s.src = src;
		s.async = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error(`could not load ${src}`));
		document.head.appendChild(s);
	});
}

async function start(): Promise<void> {
	updateRotateHint();
	window.addEventListener("resize", updateRotateHint);
	nextTip();
	const tipTimer = window.setInterval(nextTip, 6000);

	const cfg = window.HEAVENSTEEL_LOADER;
	if (!cfg) {
		window.clearInterval(tipTimer);
		const local = window.HEAVENSTEEL_ENV === "local";
		showFatal(
			"No game build yet",
			local
				? "The local stack has no WebGL build loaded. Build one (HSWeb.BuildWebGL), then restart with: bun run dev:web --build <apps/unity/Builds/web/<version>>"
				: "The game build hasn't been published yet. Try again in a few minutes.",
			"noBuild",
		);
		return;
	}
	if (!hasWebGL2()) {
		window.clearInterval(tipTimer);
		showFatal(
			"WebGL 2 isn't available",
			"Heavensteel needs WebGL 2. Try a current Chrome, Edge, Firefox or Safari, and check that hardware acceleration is on.",
			"noWebGL2",
		);
		return;
	}

	const v = pickVariant(cfg);
	const params = new URLSearchParams(location.search);
	const dprParam = Number(params.get("dpr"));
	const devicePixelRatio =
		dprParam > 0 && dprParam <= 4 ? dprParam : window.devicePixelRatio || 1;

	canvas.addEventListener("contextmenu", (e) => e.preventDefault());
	canvas.addEventListener("webglcontextlost", () =>
		reportError(
			"webglContextLost",
			liveMatch() ? "WebGL context lost in a live match" : "WebGL context lost",
		),
	);
	canvas.addEventListener("pointerdown", () => canvas.focus());

	setProgress(0);
	try {
		await loadScript(v.loaderUrl);
		const create = window.createUnityInstance;
		if (!create)
			throw new Error("the Unity loader didn't define createUnityInstance");
		const config: Record<string, unknown> = {
			dataUrl: v.dataUrl,
			frameworkUrl: v.frameworkUrl,
			codeUrl: v.codeUrl,
			streamingAssetsUrl: cfg.streamingAssetsUrl,
			companyName: cfg.companyName,
			productName: cfg.productName,
			productVersion: cfg.productVersion,
			matchWebGLToCanvasSize: true,
			devicePixelRatio,
			// Versioned paths never change: skip revalidating Unity's IndexedDB cache.
			// Streamed bundles (StreamingAssets/units, hires) stay out of it: the
			// browser's HTTP cache already keeps them (immutable), and copying each
			// one into IndexedDB costs main-thread time while the game runs.
			cacheControl: (url: string) =>
				url.includes("/builds/") && !url.includes("/StreamingAssets/")
					? "immutable"
					: "no-store",
			showBanner: (msg: string, type: string) => {
				if (type === "error") {
					console.error("[unity]", msg);
					reportError("unityBanner", msg);
					setStatus({ error: msg });
					toast(msg);
				} else console.warn("[unity]", msg);
			},
		};
		if (v.symbolsUrl) config.symbolsUrl = v.symbolsUrl;
		const instance = await create(canvas, config, setProgress);
		window.HEAVENSTEEL_INSTANCE = instance;
		window.unityInstance = instance;
		window.clearInterval(tipTimer);
		setProgress(1);
		loading.classList.add("done");
		window.setTimeout(() => {
			loading.hidden = true;
		}, 400);
		if (window.HEAVENSTEEL_STATUS.scene === "loading")
			setStatus({ scene: "boot" });
		canvas.focus();
	} catch (e) {
		window.clearInterval(tipTimer);
		const msg = e instanceof Error ? e.message : String(e);
		console.error("[heavensteel] load failed", e);
		reportError("loadFailed", msg, e instanceof Error ? e.stack : undefined);
		showFatal(
			"The game couldn't start",
			`${msg}. Reload to try again; if it keeps failing, try another browser.`,
			"loadFailed",
		);
	}
}

void start();

export {};
